import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, FileSignature, Wallet, Scissors,
  Eye, CreditCard, PackageCheck,
} from 'lucide-react';
import SEO from '../components/SEO';

const STEPS = [
  {
    icon: Sparkles,
    title: 'Design Consultation',
    body: 'We begin with a private conversation — over WhatsApp, video call, or in our Lucknow studio — to understand the moment, the silhouette, and the sentiment behind your piece.',
  },
  {
    icon: FileSignature,
    title: 'Order Confirmation',
    body: 'Once the design, fabric, and embroidery are finalised, we share a confirmation note detailing your bespoke specifications, timeline, and pricing.',
  },
  {
    icon: Wallet,
    title: '50% Advance Payment',
    body: 'A 50% advance secures your slot on our atelier calendar and releases fabric and karigar resources to begin work.',
  },
  {
    icon: Scissors,
    title: 'Handcrafting Process',
    body: 'Master karigars commence the slow art of cutting, embroidering and finishing your piece. Most commissions take six to eight weeks; intricate bridal work may take longer.',
  },
  {
    icon: Eye,
    title: 'Final Preview',
    body: 'Once complete, we share photographs and a short video for your review. Minor refinements are gladly accommodated before final dispatch.',
  },
  {
    icon: CreditCard,
    title: 'Balance Payment',
    body: 'The remaining 50% is settled before delivery. We accept bank transfer, UPI and international wire — details shared on confirmation.',
  },
  {
    icon: PackageCheck,
    title: 'Delivery',
    body: 'Your commission is hand-packed in our heritage box with a hand-written note and dispatched via insured courier. A tracking link is shared at every stage.',
  },
];

const OrderProcessPage = () => {
  return (
    <section className="policy-page" data-testid="order-process-page">
      <SEO
        title="Order Process — The Journey of a Commission"
        description="From the first consultation to the heritage box at your door — the seven gentle stages of every Silk & Sentiment commission."
        path="/order-process"
        type="article"
      />
      <div className="luxury-container">
        <Link to="/" className="policy-back" data-testid="policy-back">
          <ArrowLeft size={16} />
          <span>Back to Home</span>
        </Link>

        <header className="policy-hero">
          <p className="policy-eyebrow">Made-to-Order</p>
          <h1 className="heading-1 policy-title">The Journey of a Commission</h1>
          <span className="policy-rule" aria-hidden="true" />
          <p className="policy-lead">
            Every Silk &amp; Sentiment piece is handcrafted especially for you. From the first
            conversation to the heritage box at your door, here are the seven gentle stages of
            our atelier process.
          </p>
        </header>

        <div className="process-steps" role="list">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                className="process-step"
                role="listitem"
                data-testid={`process-step-${i + 1}`}
              >
                <span className="process-step__number">{String(i + 1).padStart(2, '0')}</span>
                <span className="process-step__icon" aria-hidden="true">
                  <Icon size={22} strokeWidth={1.4} />
                </span>
                <div className="process-step__body">
                  <h3 className="process-step__title">{s.title}</h3>
                  <p className="process-step__copy">{s.body}</p>
                </div>
              </article>
            );
          })}
        </div>

        <div className="process-cta-card">
          <h2 className="heading-2">A 50% advance confirms your commission</h2>
          <p>
            Reach out with the moment you wish to honour. Our atelier will respond within 48
            hours with a private consultation slot.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary" data-testid="process-cta-contact">
              Begin a Consultation
            </Link>
            <Link to="/returns-exchange" className="btn-secondary" data-testid="process-cta-policy">
              View Returns &amp; Exchange
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrderProcessPage;
